<template>
  <div class="permission-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>权限管理</span>
          <el-button type="primary" @click="handleCreate">新增权限</el-button>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="权限名称">
          <el-input
            v-model="queryParams.permissionName"
            placeholder="请输入权限名称"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="权限类型">
          <el-select v-model="queryParams.permissionType" placeholder="全部" clearable style="width: 150px">
            <el-option label="菜单" value="MENU" />
            <el-option label="按钮" value="BUTTON" />
            <el-option label="接口" value="API" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 120px">
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 权限列表 -->
      <el-table
        v-loading="loading"
        :data="permissionList"
        border
        style="width: 100%"
        row-key="id"
        :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
        default-expand-all
      >
        <el-table-column prop="permissionName" label="权限名称" width="200" />
        <el-table-column prop="permissionCode" label="权限编码" width="150" />
        <el-table-column prop="permissionType" label="权限类型" width="100">
          <template #default="{ row }">
            {{ getPermissionTypeName(row.permissionType) }}
          </template>
        </el-table-column>
        <el-table-column prop="resource" label="资源路径" width="200" />
        <el-table-column prop="method" label="请求方法" width="100">
          <template #default="{ row }">
            <el-tag v-if="row.method" size="small">{{ row.method }}</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip />
        <el-table-column prop="sortOrder" label="排序" width="80" />
        <el-table-column prop="status" label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.pageNum"
          v-model:page-size="queryParams.pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑权限对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      @close="handleDialogClose"
    >
      <el-form
        ref="permissionFormRef"
        :model="permissionForm"
        :rules="permissionFormRules"
        label-width="100px"
      >
        <el-form-item label="权限名称" prop="permissionName">
          <el-input v-model="permissionForm.permissionName" />
        </el-form-item>
        <el-form-item label="权限编码" prop="permissionCode">
          <el-input v-model="permissionForm.permissionCode" />
        </el-form-item>
        <el-form-item label="权限类型" prop="permissionType">
          <el-select v-model="permissionForm.permissionType" style="width: 100%">
            <el-option label="菜单" value="MENU" />
            <el-option label="按钮" value="BUTTON" />
            <el-option label="接口" value="API" />
          </el-select>
        </el-form-item>
        <el-form-item label="资源路径" prop="resource">
          <el-input v-model="permissionForm.resource" placeholder="如：/api/user/list" />
        </el-form-item>
        <el-form-item label="请求方法" prop="method">
          <el-select v-model="permissionForm.method" clearable placeholder="请选择" style="width: 100%">
            <el-option label="GET" value="GET" />
            <el-option label="POST" value="POST" />
            <el-option label="PUT" value="PUT" />
            <el-option label="DELETE" value="DELETE" />
            <el-option label="PATCH" value="PATCH" />
          </el-select>
        </el-form-item>
        <el-form-item label="父权限" prop="parentId">
          <el-tree-select
            v-model="permissionForm.parentId"
            :data="permissionTreeData"
            :props="{ label: 'permissionName', value: 'id', children: 'children' }"
            placeholder="请选择父权限"
            clearable
            style="width: 100%"
            :render-after-expand="false"
          />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="permissionForm.description" type="textarea" :rows="3" />
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number v-model="permissionForm.sortOrder" :min="0" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="permissionForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  getPermissionList,
  getPermissionById,
  createPermission,
  updatePermission,
  deletePermission,
  getAllPermissions
} from '../api/sysPermission'

// 数据
const loading = ref(false)
const permissionList = ref([])
const total = ref(0)
const permissionTreeData = ref([])
const queryParams = reactive({
  permissionName: '',
  permissionType: '',
  status: null,
  pageNum: 1,
  pageSize: 10
})

// 对话框
const dialogVisible = ref(false)
const dialogTitle = ref('新增权限')
const isEdit = ref(false)
const permissionFormRef = ref(null)
const permissionForm = reactive({
  id: null,
  permissionName: '',
  permissionCode: '',
  permissionType: 'BUTTON',
  resource: '',
  method: '',
  parentId: null,
  description: '',
  sortOrder: 0,
  status: 1
})

const permissionFormRules = {
  permissionName: [{ required: true, message: '请输入权限名称', trigger: 'blur' }],
  permissionCode: [{ required: true, message: '请输入权限编码', trigger: 'blur' }],
  permissionType: [{ required: true, message: '请选择权限类型', trigger: 'change' }]
}

// 方法
const getPermissionTypeName = (type) => {
  const typeMap = {
    'MENU': '菜单',
    'BUTTON': '按钮',
    'API': '接口'
  }
  return typeMap[type] || type
}

const loadPermissionList = async () => {
  loading.value = true
  try {
    const res = await getPermissionList(queryParams)
    if (res.code === '000000') {
      permissionList.value = res.data.records || []
      total.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取权限列表失败:', error)
  } finally {
    loading.value = false
  }
}

const loadPermissionTree = async () => {
  try {
    const res = await getAllPermissions()
    if (res.code === '000000') {
      permissionTreeData.value = buildPermissionTree(res.data || [])
    }
  } catch (error) {
    console.error('获取权限树失败:', error)
  }
}

const buildPermissionTree = (permissions) => {
  const map = new Map()
  const roots = []

  // 创建节点映射
  permissions.forEach(perm => {
    map.set(perm.id, { ...perm, children: [] })
  })

  // 构建树
  permissions.forEach(perm => {
    const node = map.get(perm.id)
    if (perm.parentId == null) {
      roots.push(node)
    } else {
      const parent = map.get(perm.parentId)
      if (parent) {
        parent.children.push(node)
      } else {
        roots.push(node)
      }
    }
  })

  return roots
}

const handleSearch = () => {
  queryParams.pageNum = 1
  loadPermissionList()
}

const handleReset = () => {
  queryParams.permissionName = ''
  queryParams.permissionType = ''
  queryParams.status = null
  queryParams.pageNum = 1
  loadPermissionList()
}

const handleCreate = () => {
  dialogTitle.value = '新增权限'
  isEdit.value = false
  Object.assign(permissionForm, {
    id: null,
    permissionName: '',
    permissionCode: '',
    permissionType: 'BUTTON',
    resource: '',
    method: '',
    parentId: null,
    description: '',
    sortOrder: 0,
    status: 1
  })
  dialogVisible.value = true
}

const handleEdit = async (row) => {
  dialogTitle.value = '编辑权限'
  isEdit.value = true
  try {
    const res = await getPermissionById(row.id)
    if (res.code === '000000') {
      Object.assign(permissionForm, res.data)
    }
    dialogVisible.value = true
  } catch (error) {
    console.error('获取权限详情失败:', error)
  }
}

const handleSubmit = async () => {
  if (!permissionFormRef.value) return
  await permissionFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const res = isEdit.value ? await updatePermission(permissionForm) : await createPermission(permissionForm)
        if (res.code === '000000') {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          dialogVisible.value = false
          loadPermissionList()
          loadPermissionTree()
        }
      } catch (error) {
        console.error('保存权限失败:', error)
      }
    }
  })
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm('确定要删除该权限吗？', '提示', {
      type: 'warning',
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })
    const res = await deletePermission(row.id)
    if (res.code === '000000') {
      ElMessage.success('删除成功')
      loadPermissionList()
      loadPermissionTree()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除权限失败:', error)
    }
  }
}

const handleDialogClose = () => {
  permissionFormRef.value?.resetFields()
}

const handleSizeChange = (size) => {
  queryParams.pageSize = size
  loadPermissionList()
}

const handlePageChange = (page) => {
  queryParams.pageNum = page
  loadPermissionList()
}

// 生命周期
onMounted(() => {
  loadPermissionList()
  loadPermissionTree()
})
</script>

<style scoped>
.permission-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>

